import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Plus, UserX, UserCheck, UserCog, Pencil, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS =
  "rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm text-[#0F172A] placeholder:text-[#94A3B8]";

const EMPTY_FORM = { name: "", email: "", phone: "", role: "", warehouse_id: "", warehouse_name: "" };

const ROLES = [
  { value: "admin",                    label: "Admin"                    },
  { value: "supervisor",               label: "Supervisor"               },
  { value: "relationship_manager", label: "Relationship Manager" },
  { value: "warehouse_manager",        label: "Warehouse Manager"        },
  { value: "driver",                   label: "Driver"                   },
  { value: "helper",                   label: "Helper"                   },
];

const ROLE_BADGE = {
  admin:                    "bg-indigo-50 text-indigo-700 border-indigo-200",
  supervisor:               "bg-purple-50 text-purple-700 border-purple-200",
  relationship_manager: "bg-teal-50 text-teal-700 border-teal-200",
  warehouse_manager:        "bg-amber-50 text-amber-700 border-amber-200",
  driver:                   "bg-blue-50 text-blue-700 border-blue-200",
  helper:                   "bg-slate-100 text-slate-600 border-slate-200",
  superadmin:               "bg-[#1B2A6B]/10 text-[#1B2A6B] border-[#1B2A6B]/20",
};

const ROLE_LABEL = {
  admin:                    "Admin",
  supervisor:               "Supervisor",
  relationship_manager: "Relationship Manager",
  warehouse_manager:        "Warehouse Manager",
  driver:                   "Driver",
  helper:                   "Helper",
  superadmin:               "Super Admin",
};

const RoleBadge = ({ role }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${
      ROLE_BADGE[role] || "bg-gray-100 text-gray-600 border-gray-200"
    }`}
  >
    {ROLE_LABEL[role] || role}
  </span>
);

const StatusBadge = ({ isActive }) =>
  isActive ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm bg-green-50 text-green-700 border-green-200">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm bg-red-50 text-red-600 border-red-200">
      <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />
      Inactive
    </span>
  );

const FormField = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">
      {label}
    </label>
    {children}
  </div>
);

export default function UsersPage() {
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showAdd,     setShowAdd]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [editUser,    setEditUser]    = useState(null);
  const [editForm,    setEditForm]    = useState({ name: "", phone: "", role: "" });
  const [editSaving,  setEditSaving]  = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ password: "", confirm: "" });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [warehouses, setWarehouses] = useState([]);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(data);
    } catch {
      toast.error("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    axios.get(`${API}/warehouses`, { withCredentials: true })
      .then(r => setWarehouses(r.data))
      .catch(() => {});
  }, []);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const resetForm = () => setForm(EMPTY_FORM);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.role) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (form.role === "warehouse_manager" && !form.warehouse_id) {
      toast.error("Please select a warehouse for the Warehouse Manager.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/users`, form, { withCredentials: true });
      toast.success("User created! Default password: Welcome@123", {
        duration: 8000,
        description: "Share this password with the new user.",
      });
      setShowAdd(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this user? They will no longer be able to log in.")) return;
    try {
      await axios.patch(`${API}/users/${id}/deactivate`, {}, { withCredentials: true });
      toast.success("User deactivated.");
      setUsers((prev) => prev.map((u) => u.id === id ? { ...u, is_active: false } : u));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to deactivate user.");
    }
  };

  const handleActivate = async (id) => {
    try {
      await axios.patch(`${API}/users/${id}/activate`, {}, { withCredentials: true });
      toast.success("User activated.");
      setUsers((prev) => prev.map((u) => u.id === id ? { ...u, is_active: true } : u));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to activate user.");
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({ name: user.name || "", phone: user.phone || "", role: user.role || "" });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) { toast.error("Name is required."); return; }
    if (!editForm.role) { toast.error("Role is required."); return; }
    setEditSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/users/${editUser.id}`,
        { name: editForm.name.trim(), phone: editForm.phone.trim(), role: editForm.role },
        { withCredentials: true }
      );
      setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, ...data } : u));
      toast.success("User updated.");
      setEditUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user.");
    } finally {
      setEditSaving(false);
    }
  };

  const openPasswordReset = (user) => {
    setPasswordUser(user);
    setPasswordForm({ password: "", confirm: "" });
    setShowPasswords(false);
  };

  const closePasswordReset = () => {
    setPasswordUser(null);
    setPasswordForm({ password: "", confirm: "" });
    setShowPasswords(false);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (passwordForm.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (passwordForm.password !== passwordForm.confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setPasswordSaving(true);
    try {
      await axios.patch(
        `${API}/users/${passwordUser.id}/password`,
        { new_password: passwordForm.password },
        { withCredentials: true }
      );
      toast.success(`Password updated for ${passwordUser.name}.`);
      closePasswordReset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password.");
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A6B]">Users</h2>
          <p className="text-xs text-[#475569] mt-0.5">
            {users.length} team member{users.length !== 1 ? "s" : ""}
          </p>
        </div>
        <Button
          data-testid="add-user-btn"
          onClick={() => { resetForm(); setShowAdd(true); }}
          className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-10 px-5 border-b-2 border-[#FFD700]"
        >
          <Plus size={14} className="mr-1.5" />
          Add User
        </Button>
      </div>

      {/* Default password note */}
      <div className="bg-amber-50 border border-amber-200 border-l-4 border-l-amber-400 px-4 py-3 flex items-start gap-2">
        <span className="text-amber-600 text-xs font-semibold mt-0.5">Note:</span>
        <p className="text-xs text-amber-700">
          All newly created users are assigned the default password{" "}
          <code className="font-bold bg-amber-100 px-1 py-0.5 rounded">Welcome@123</code>.
          Please share this with them and advise changing it on first login.
        </p>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0]">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-sm text-[#94A3B8]">
            Loading...
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <UserCog size={40} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">
              No team members yet.{" "}
              <button
                onClick={() => { resetForm(); setShowAdd(true); }}
                className="text-[#1B2A6B] font-semibold underline underline-offset-2"
              >
                Add your first user
              </button>
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                  {["#", "Name", "Email", "Phone", "Role", "Status", "Created On", ""].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((user, idx) => (
                  <tr
                    key={user.id}
                    data-testid={`user-row-${idx}`}
                    className="border-b border-[#E2E8F0] hover:bg-[#F9FAFB] transition-colors"
                  >
                    <td className="px-4 py-3 text-[#94A3B8] text-xs font-mono">
                      {idx + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-[#1B2A6B] flex items-center justify-center shrink-0">
                          <span className="text-[10px] font-bold text-[#FFD700]">
                            {(user.name || "U")
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .toUpperCase()
                              .slice(0, 2)}
                          </span>
                        </div>
                        <span className="font-semibold text-[#0F172A]">
                          {user.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#475569]">{user.email}</td>
                    <td className="px-4 py-3 text-[#475569]">{user.phone || "—"}</td>
                    <td className="px-4 py-3">
                      <div>
                        <RoleBadge role={user.role} />
                        {user.role === "warehouse_manager" && user.warehouse_name && (
                          <p className="text-[10px] text-[#94A3B8] mt-1 font-medium">{user.warehouse_name}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge isActive={user.is_active !== false} />
                    </td>
                    <td className="px-4 py-3 text-[#475569] whitespace-nowrap text-xs">
                      {user.created_at
                        ? format(new Date(user.created_at), "dd MMM yyyy")
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`edit-user-${idx}`}
                          onClick={() => openEdit(user)}
                          className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#475569] hover:text-[#1B2A6B] border border-[#E2E8F0] hover:border-[#1B2A6B] transition-colors px-2 py-1 rounded-sm"
                          title="Edit user"
                        >
                          <Pencil size={11} />Edit
                        </button>
                        <button
                          data-testid={`reset-password-${idx}`}
                          onClick={() => openPasswordReset(user)}
                          className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#475569] hover:text-[#1B2A6B] border border-[#E2E8F0] hover:border-[#1B2A6B] transition-colors px-2 py-1 rounded-sm"
                          title="Change user password"
                        >
                          <KeyRound size={11} />Password
                        </button>
                        {user.is_active !== false ? (
                          <button
                            data-testid={`deactivate-user-${idx}`}
                            onClick={() => handleDeactivate(user.id)}
                            className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-orange-500 hover:text-orange-700 border border-orange-200 hover:border-orange-400 hover:bg-orange-50 transition-colors px-2 py-1 rounded-sm"
                          >
                            <UserX size={12} />Deactivate
                          </button>
                        ) : (
                          <button
                            data-testid={`activate-user-${idx}`}
                            onClick={() => handleActivate(user.id)}
                            className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-green-600 hover:text-green-800 border border-green-200 hover:border-green-400 hover:bg-green-50 transition-colors px-2 py-1 rounded-sm"
                          >
                            <UserCheck size={12} />Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add User Dialog */}
      <Dialog
        open={showAdd}
        onOpenChange={(open) => {
          if (!open) resetForm();
          setShowAdd(open);
        }}
      >
        <DialogContent className="max-w-lg rounded-none p-0 gap-0">
          {/* Header */}
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">
              Add New User
            </DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">
              Enter the team member details below
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 space-y-4">
              <FormField label="Full Name *">
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Priya Mehta"
                  data-testid="user-name-input"
                  className={INPUT_CLS}
                />
              </FormField>

              <FormField label="Email Address *">
                <Input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="e.g. priya@gosmart.com"
                  data-testid="user-email-input"
                  className={INPUT_CLS}
                />
              </FormField>

              <FormField label="Phone *">
                <Input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="e.g. 9876543210"
                  data-testid="user-phone-input"
                  className={INPUT_CLS}
                />
              </FormField>

              <FormField label="Role *">
                <Select
                  value={form.role}
                  onValueChange={(v) => setForm((f) => ({ ...f, role: v, warehouse_id: "", warehouse_name: "" }))}
                >
                  <SelectTrigger
                    data-testid="user-role-select"
                    className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm text-[#0F172A]"
                  >
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value} className="rounded-none">
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              {/* Warehouse — only for Warehouse Manager */}
              {form.role === "warehouse_manager" && (
                <FormField label="Assigned Warehouse *">
                  <Select
                    value={form.warehouse_id}
                    onValueChange={(v) => {
                      const wh = warehouses.find(w => w.id === v);
                      setForm(f => ({ ...f, warehouse_id: v, warehouse_name: wh?.warehouse_name || "" }));
                    }}
                  >
                    <SelectTrigger
                      data-testid="user-warehouse-select"
                      className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm text-[#0F172A]"
                    >
                      <SelectValue placeholder={warehouses.length === 0 ? "No warehouses — add via Master Data" : "Select warehouse"} />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {warehouses.map((w) => (
                        <SelectItem key={w.id} value={w.id} className="rounded-none">
                          <span className="font-medium">{w.warehouse_name}</span>
                          {w.city_name && <span className="text-[#94A3B8] ml-1.5 text-xs">— {w.city_name}</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              )}
            </div>

            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button
                type="button"
                onClick={() => { resetForm(); setShowAdd(false); }}
                data-testid="user-cancel-btn"
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] hover:text-[#0F172A] transition-colors"
              >
                Cancel
              </button>
              <Button
                type="submit"
                data-testid="user-save-btn"
                disabled={saving}
                className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60"
              >
                {saving ? "Creating..." : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Edit User Dialog ── */}
      <Dialog open={!!editUser} onOpenChange={o => !o && setEditUser(null)}>
        <DialogContent className="max-w-sm rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Edit User</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">{editUser?.email}</p>
          </div>
          <form onSubmit={handleEditSubmit} className="px-6 py-5 space-y-4">
            <div>
              <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1.5">Name *</label>
              <Input
                value={editForm.name}
                onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
                className={INPUT_CLS}
                data-testid="edit-user-name"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1.5">Phone</label>
              <Input
                value={editForm.phone}
                onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="Phone number"
                className={INPUT_CLS}
                data-testid="edit-user-phone"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1.5">Role *</label>
              <Select value={editForm.role} onValueChange={v => setEditForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className={`${INPUT_CLS} w-full`} data-testid="edit-user-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value} className="rounded-none">{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="pt-2 gap-2">
              <button type="button" onClick={() => setEditUser(null)}
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">
                Cancel
              </button>
              <Button type="submit" disabled={editSaving} data-testid="edit-user-submit"
                className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">
                {editSaving ? "Saving…" : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Reset User Password Dialog ── */}
      <Dialog open={!!passwordUser} onOpenChange={open => !open && closePasswordReset()}>
        <DialogContent className="max-w-sm rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Change User Password</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">{passwordUser?.name} · {passwordUser?.email}</p>
          </div>
          <form onSubmit={handlePasswordReset} className="px-6 py-5 space-y-4">
            <div>
              <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1.5">New Password *</label>
              <div className="relative">
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={passwordForm.password}
                  onChange={e => setPasswordForm(form => ({ ...form, password: e.target.value }))}
                  minLength={6}
                  autoComplete="new-password"
                  className={`${INPUT_CLS} pr-10`}
                  data-testid="reset-password-input"
                  autoFocus
                />
                <button type="button" onClick={() => setShowPasswords(value => !value)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]" aria-label={showPasswords ? "Hide passwords" : "Show passwords"}>
                  {showPasswords ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1.5">Confirm Password *</label>
              <Input
                type={showPasswords ? "text" : "password"}
                value={passwordForm.confirm}
                onChange={e => setPasswordForm(form => ({ ...form, confirm: e.target.value }))}
                minLength={6}
                autoComplete="new-password"
                className={INPUT_CLS}
                data-testid="reset-password-confirm"
              />
            </div>
            <p className="text-[11px] text-[#94A3B8]">The password must contain at least 6 characters.</p>
            <DialogFooter className="pt-2 gap-2">
              <button type="button" onClick={closePasswordReset} className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">Cancel</button>
              <Button type="submit" disabled={passwordSaving} data-testid="reset-password-submit" className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">
                {passwordSaving ? "Updating…" : "Update Password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
